import { defineComponent as _defineComponent } from 'vue'
import type { Maybe } from '@/helpers/types';
import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import type { SingleTitleCampaign } from '@/features/sponsored-recommendations/types';
import type { PosterSponsoredRecImages } from '@/interfaces/components/poster/PosterGraphql';

import { computed, ref, watch } from 'vue';
import { useDocumentVisibility, useElementSize, useMediaQuery } from '@vueuse/core';

import { BackgroundVideoSize, SraEventAction, SraPlacement } from '@/@types/graphql-types';
import { ImpressionTrackingEvents } from '@/enums/events';
import { SnowplowContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { useBackendTracking } from '@/helpers/composables/useBackendTracking';

import PictureComp from '@/components/picture/PictureComp.vue';

type TrackingWatchedPointsInPercentage = (typeof TrackingWatchedPointsInPercentage)[number];
type TrackingWatchedPointsInSeconds = (typeof TrackingWatchedPointsInSeconds)[number];
type VideoObject = Partial<Record<BackgroundVideoSize, string>>;

export default /*#__PURE__*/_defineComponent({
  __name: 'SponsoredRecommendationVideoSearch',
  props: {
    sponsoredAd: null,
    sponsoredAdCampaign: null,
    lazy: { type: Boolean, default: true },
    additionalContexts: { default: () => [] },
    isCinemaSR: { type: Boolean, default: false },
    sraPlacement: null,
    showPoster: { type: Boolean, default: false }
  },
  emits: ["onPosterClickOut", "imageError", "videoError", "onVideoEnded"],
  setup(__props: any, { emit: emits }: { emit: ({
	(e: 'onPosterClickOut'): void;
	(e: 'imageError', src: Maybe<string>): void;
	(e: 'videoError', src: Maybe<string>): void;
	(e: 'onVideoEnded', hasVideoEnded: boolean): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { sponsoredAd: SponsoredAdFragment, sponsoredAdCampaign: SingleTitleCampaign, lazy: boolean, additionalContexts: SnowplowContext[], isCinemaSR: boolean, sraPlacement?: SraPlacement, showPoster: boolean };

const TrackingWatchedPointsInPercentage = [0, 25, 50, 75, 90, 100] as const;

const TrackingWatchedPointsInSeconds = [2, 6, 15] as const;





const { trackSraEvent } = useBackendTracking();
const visibility = useDocumentVisibility();

const campaignNode = computed(() => props.sponsoredAdCampaign.node);

const backgroundVideos = computed(() => {
	const videoSizes: VideoObject = {};

	props.sponsoredAdCampaign?.backgroundVideos?.map(options => {
		if (!options?.size) return;
		videoSizes[options.size] = options?.videoURL;
	});

	return videoSizes;
});

const isSmallViewport = useMediaQuery('(max-width: 525px)');
const srRoot = ref<HTMLElement | null>(null);
const { width: srWidth } = useElementSize(srRoot);

// Mirror each CSS rule with its matching sized asset url
//   - search_box   -> @container (max-width: 525px) -> use SR column width
//   - search_page  -> @media (max-width: 525px)     -> use viewport
const videoSrc = computed(() => {
	const isSearchbox = props.sraPlacement === SraPlacement.SearchBox;
	// srWidth starts at 0 until ResizeObserver firestreat 0 i.e. not measured yet, fall back to the viewport check.
	const useMobile = isSearchbox && srWidth.value > 0 ? srWidth.value <= 525 : isSmallViewport.value;
	const desktopUrl = props.sponsoredAdCampaign.promotionalVideo?.url;
	// fallback to main video asset if small doesn't exist
	return useMobile ? backgroundVideos.value.SMALL || desktopUrl : desktopUrl;
});

const posterPath = computed(() => props.sponsoredAdCampaign.node.content.fullPath);

const posterImageOverride = computed<PosterSponsoredRecImages | null>(() => {
	let posterUrl = props.sponsoredAdCampaign?.posterOverride || campaignNode.value.content.posterUrl;
	if (!posterUrl) return null;

	if (posterUrl.startsWith('/')) {
		posterUrl = `${JW_CONFIG.IMAGESCALER_URL}${posterUrl.replace('{profile}', 's332')}`;
	}

	return {
		imageUrl: posterUrl,
		sourceSets: [
			{
				srcSet: posterUrl.replace(/\.{?\w+}?$/, '.avif'),
				imageProp: {
					extension: '.avif',
					type: 'image/avif',
				},
				sourceProp: {
					viewport: 'desktop',
				},
				media: null,
			},
		],
	};
});

const videoElement = ref<HTMLVideoElement | null>(null);
const isMuted = ref(true);
const isFading = ref(false);

// Only fade on the 3/2 (mobile)
const shouldFade = computed(() => isFading.value && srWidth.value > 0 && srWidth.value <= 525);

function onVideoStateChanged(eventName: string) {
	if (eventName !== 'play') return;
	setTimeout(() => (isFading.value = true), 1500);
}

function onMouseEnter() {
	isFading.value = false;
}

function onMouseLeave() {
	isFading.value = true;
}

const volumeIcon = computed(() => {
	const filename = isMuted.value ? 'volume-xmark' : 'volume-high';
	return `/${ASSETS_DIR}/img/sponsored-recommendation-assets/${filename}.svg`;
});

watch(visibility, (current, previous) => {
	if (!videoElement.value) return;
	if (current === 'visible' && previous === 'hidden') videoElement.value.play();
	if (current === 'hidden' && previous === 'visible') videoElement.value.pause();
});

function onVisibilityPlaybackControl(isVisible: boolean) {
	if (!videoElement.value) return;
	isVisible ? videoElement.value.play() : videoElement.value.pause();
}

function toggleMute() {
	isMuted.value = !isMuted.value;

	if (videoElement.value) {
		videoElement.value.muted = isMuted.value;
	}

	if (!campaignNode.value) return;

	const titleContext = new SnowplowTitleContextGraphql(
		campaignNode.value?.objectId,
		campaignNode.value?.objectType,
		campaignNode.value?.__typename === 'Season' ? campaignNode.value?.content.seasonNumber : null
	);

	TrackingHelper.trackEvent(
		ImpressionTrackingEvents.SPONSORED_RECOMMENDATIONS,
		{
			action: 'toggle_mute',
			value: isMuted.value ? 1 : 0,
		},
		[titleContext]
	);
}

const videoDuration = ref(0);
const lastTrackedPointPercentage = ref<TrackingWatchedPointsInPercentage | -1>(-1);
const lastTrackedPointSeconds = ref<TrackingWatchedPointsInSeconds | -1>(-1);

function setVideoDuration() {
	if (!videoElement.value) return;
	videoDuration.value = videoElement.value.duration;
}

function onTimeUpdate() {
	const currentTime = videoElement.value?.currentTime ?? 0;

	if (lastTrackedPointPercentage.value < 100 && videoDuration.value > 0) {
		const percentageViewed = Math.ceil((currentTime / videoDuration.value) * 100);
		const next = TrackingWatchedPointsInPercentage.find(p => p > lastTrackedPointPercentage.value);

		if (next != null && percentageViewed >= next) {
			lastTrackedPointPercentage.value = next;
			trackSraEvent({
				action: SraEventAction.VideoView,
				sponsoredAd: () => props.sponsoredAd,
				progress: next,
			});
		}
	}

	if (lastTrackedPointSeconds.value < 15) {
		const secondsViewed = Math.floor(currentTime);
		const next = TrackingWatchedPointsInSeconds.find(p => p > lastTrackedPointSeconds.value);

		if (next != null && secondsViewed >= next) {
			lastTrackedPointSeconds.value = next;
			trackSraEvent({
				action: SraEventAction.VideoViewSeconds,
				sponsoredAd: () => props.sponsoredAd,
				progress: next,
			});
		}
	}
}

function onVideoEnded() {
	if (!videoElement.value) return;
	emits('onVideoEnded', true);
	videoElement.value.play();
}

return { __sfc: true,TrackingWatchedPointsInPercentage, TrackingWatchedPointsInSeconds, props, emits, trackSraEvent, visibility, campaignNode, backgroundVideos, isSmallViewport, srRoot, srWidth, videoSrc, posterPath, posterImageOverride, videoElement, isMuted, isFading, shouldFade, onVideoStateChanged, onMouseEnter, onMouseLeave, volumeIcon, onVisibilityPlaybackControl, toggleMute, videoDuration, lastTrackedPointPercentage, lastTrackedPointSeconds, setVideoDuration, onTimeUpdate, onVideoEnded, SraPlacement, PictureComp }
}

})